import { t } from "@lingui/macro";
import { ArrowRight, DownloadSimple, PencilSimple, Swatches } from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import { useAuthStore } from "@/client/stores/auth";

const steps = [
  {
    number: "01",
    icon: <Swatches weight="duotone" className="h-8 w-8" />,
    title: "Pick a Template",
    description:
      "Choose from 12+ professional templates, all designed to pass ATS screening. Customize colors, fonts, and layouts to match your style.",
    screenshot: "/screenshots/resume-pdf.png",
    screenshotAlt: "Template picker showing 12+ ATS-verified resume templates",
  },
  {
    number: "02",
    icon: <PencilSimple weight="duotone" className="h-8 w-8" />,
    title: "Let AI Write Your Resume",
    description:
      "Add your experience and let AI generate professional bullet points, quantified achievements, and tailored content. Improve any text with a single click.",
    screenshot: "/screenshots/ai-writer-after.png",
    screenshotAlt: "AI resume writing with one-click improve, grammar, expand, quantify, and tone actions",
  },
  {
    number: "03",
    icon: <DownloadSimple weight="duotone" className="h-8 w-8" />,
    title: "Download & Apply",
    description:
      "Export a PDF on every plan, or DOCX on Pro and above. Perfectly formatted for ATS systems. Tailor your resume to each job description and start getting callbacks.",
    screenshot: "/screenshots/export.png",
    screenshotAlt: "Export resume as PDF on every plan, or DOCX on Pro and above",
  },
];

const StepScreenshot = ({ src, alt }: { src: string; alt: string }) => {
  return (
    <div className="mt-5 overflow-hidden rounded-lg border border-border/50 bg-secondary-accent/30">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        className="w-full object-cover"
      />
    </div>
  );
};

export const HowItWorksSection = () => {
  const isLoggedIn = useAuthStore((state) => !!state.user);

  return (
    <section id="how-it-works" className="relative bg-secondary-accent/50 py-24 sm:py-32">
      <div className="container">
        <motion.div
          className="mx-auto max-w-2xl text-center"
          viewport={{ once: true }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <p className="text-sm font-semibold uppercase tracking-wider text-primary">
            {t`How It Works`}
          </p>
          <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
            {t`Build your resume in 3 simple steps`}
          </h2>
        </motion.div>

        <div className="mx-auto mt-16 grid max-w-5xl gap-8 lg:grid-cols-3">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0, transition: { delay: index * 0.15 } }}
              className="relative text-center"
            >
              {index < steps.length - 1 && (
                <ArrowRight
                  weight="bold"
                  className="absolute -right-4 top-12 hidden h-6 w-6 text-foreground/20 lg:block"
                />
              )}
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                {step.icon}
              </div>
              <span className="mt-4 block text-xs font-bold uppercase tracking-wider text-foreground/30">
                {t`Step`} {step.number}
              </span>
              <h3 className="mt-2 text-xl font-semibold">{step.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground/60">
                {step.description}
              </p>
              <StepScreenshot src={step.screenshot} alt={step.screenshotAlt} />
            </motion.div>
          ))}
        </div>

        <motion.div
          className="mt-12 text-center"
          viewport={{ once: true }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1, transition: { delay: 0.5 } }}
        >
          <Button asChild size="lg">
            <Link to={isLoggedIn ? "/dashboard/resumes" : "/auth/register"}>
              {isLoggedIn ? t`Go to Dashboard` : t`Start Building`}
              <ArrowRight className="ml-2" weight="bold" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
